import {
  Body,
  Box,
  Button,
  HStack,
  Heading,
  RadioButton,
  RadioButtonGroup,
  Sheet,
  VStack,
  useToast,
} from '@hover/blueprint';
import type { ChangeEvent } from 'react';
import React, { useCallback, useState } from 'react';

import { imageTracking } from 'src/modules/homeownerApp/components/Customize/tracking';
import { RemainingTextInput } from 'src/modules/homeownerApp/components/RemainingTextInput';
import {
  MAX_PROJECT_NAME_CHARACTERS,
  useCreateAiDisplayImageAndUpdateCache,
  useCreateProjectAndUpdateCache,
  useProjectNamingValidation,
} from 'src/modules/homeownerApp/components/Utils';
import {
  useDetectKeyboardOpen,
  useErrors,
  useHomeownerAppContext,
  useSuggestions,
} from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { useSaveToProjectContext } from 'src/modules/homeownerApp/providers/SaveToProjectProvider';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import { buttonPressed } from 'src/shared/telemetry';

const NewProject = () => {
  const [projectName, setProjectName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const toast = useToast();
  const [validateProjectName, { isEmpty, isTooLong, validatedName }] =
    useProjectNamingValidation();
  const createProject = useCreateProjectAndUpdateCache();
  const createAiDisplayImage = useCreateAiDisplayImageAndUpdateCache();
  const { isKeyboardOpen, viewPortHeight } = useDetectKeyboardOpen();

  const { showError, showGraphQlErrors } = useErrors();
  const { displayImageToSave: image, generatedImageToSave: generatedImage } =
    useSaveToProjectContext();
  const { setCurrentMenu, trackingData } = useHomeownerAppContext();

  const {
    metadata: { partner_id: partnerId },
  } = useTrackingMetadata();

  const { spaceType } = image || {};

  const projectNameSuggestions = useSuggestions({
    limitTo: 3,
    onlyFirstLetterCapitalized: true,
    projectName,
    spaceType,
  });

  const formatError = useCallback(
    (errorMessage: string): string =>
      t('customize.newProject.createProjectFailure', { errorMessage }),
    [t],
  );

  const handleCancelClick = () => {
    setCurrentMenu(null);
  };

  const handleSubmit = useCallback(async () => {
    buttonPressed({
      ...trackingData,
      ...imageTracking(image),
      button_location: 'Save to module',
      button_text: 'Create project',
      partner_id: partnerId,
      primary_cta: true,
      track_id: 'create-project',
    });
    setIsLoading(true);
    try {
      const { data, errors } = await createProject(validatedName, []);
      const projectId = data?.createProject?.id || '';
      const message = t('customize.newProject.createProjectSuccess', {
        projectName: validatedName,
      });

      if (!projectId || !generatedImage) return;

      await createAiDisplayImage(generatedImage, projectId);
      if (errors?.length) {
        showGraphQlErrors({ errors, formatError, isDetailed: true });
      } else {
        toast({ props: { message } });
        setCurrentMenu(HomeownerAppMenuNames.ADD_TO_PROJECT);
      }
    } catch (error) {
      showError({ error: error as Error, formatError });
    } finally {
      setIsLoading(false);
    }
    // the toast can't be a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createAiDisplayImage,
    createProject,
    formatError,
    image,
    validatedName,
    setCurrentMenu,
    showError,
    showGraphQlErrors,
    t,
  ]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    setProjectName(name);
    validateProjectName(name);
  };

  const SubmitButton = useCallback(
    () => (
      <Button
        isDisabled={isEmpty || isTooLong || !validatedName}
        isLoading={isLoading}
        onClick={handleSubmit}
        size="large"
        width="100%"
      >
        {t('customize.newProject.createProject')}
      </Button>
    ),
    [handleSubmit, isEmpty, isLoading, isTooLong, t, validatedName],
  );

  const handleSuggestionClick = (name: string) => {
    buttonPressed({
      ...trackingData,
      ...imageTracking(image),
      button_location: 'Save to module',
      button_text: name,
      partner_id: partnerId,
      primary_cta: true,
      track_id: 'suggested-project-name',
    });
    setProjectName(name);
    validateProjectName(name);
  };

  return (
    <Sheet.Contents contentKey="new-project">
      <VStack
        height={{
          base: `${viewPortHeight + (isKeyboardOpen ? 125 : -100)}px`,
          tablet: 'calc(100dvh - 135px)',
        }}
        justifyContent="space-between"
        padding={{ tablet: 200 }}
      >
        <VStack alignItems="flex-start" width="100%">
          <HStack
            display={{ base: 'flex', tablet: 'none' }}
            justifyContent="flex-end"
            width="100%"
          >
            <Body
              _hover={{ textColor: 'neutral.600' }}
              cursor="pointer"
              marginTop={0}
              onClick={handleCancelClick}
              transition="color 0.2s"
            >
              {t('customize.newProject.cancel')}
            </Body>
          </HStack>
          <Heading maxWidth="70%">{t('customize.newProject.header')}</Heading>
          <Body fontWeight={400} textColor="neutral.600">
            {t('customize.newProject.subHeader')}
          </Body>
          <Box paddingTop={500} paddingX={100} width="100%">
            <RemainingTextInput
              changeAction={handleChange}
              longLimit={MAX_PROJECT_NAME_CHARACTERS}
              placeholder={t('customize.newProject.inputPlaceholder')}
              value={projectName}
            />
          </Box>
          {isKeyboardOpen && <SubmitButton />}
        </VStack>
        <VStack alignItems="flex-start" width="100%">
          {projectNameSuggestions.length && (
            <>
              <Body fontWeight={400} textColor="neutral.600">
                {t('customize.newProject.whatAbout')}
              </Body>
              <RadioButtonGroup
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                gap={200}
                onChange={handleSuggestionClick}
              >
                {projectNameSuggestions.map(name => (
                  <RadioButton key={name} size="small" value={name}>
                    {name}
                  </RadioButton>
                ))}
              </RadioButtonGroup>
            </>
          )}
          {!isKeyboardOpen && <SubmitButton />}
        </VStack>
      </VStack>
    </Sheet.Contents>
  );
};

export { NewProject };
